import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useFormikWithDirtyForm } from "@shared";
import { useConfirmationDialogContext } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { VolunteerResponse, volunteersApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { ApiErrorCode, getApiErrorCode } from "@/common/helper/apiErrorHelper";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { VolunteerFormType } from "@/pages/system-settings/volunteers/components/VolunteerForm.types";
import { volunteerValidationSchema } from "@/pages/system-settings/volunteers/components/VolunteerForm.utils";
import { QueryKey } from "@/query/Query";
import { SystemSettingsTab, systemSettingsPath } from "@/router/SystemSettingsRoutes";
import { useTranslation } from "@/translations";

export function useVolunteerPersonalData(volunteer: VolunteerResponse) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();
  const dropdownOptions = useDropdownOptions();

  const id = volunteer.volunteerId;

  const updateVolunteerMutation = useMutation({
    mutationFn: (values: VolunteerFormType) =>
      volunteersApi.updateVolunteer(
        {
          id,
          updateVolunteerRequest: {
            username: values.username,
            salutation: values.salutation,
            title: values.title,
            lastName: values.lastName,
            firstName: values.firstName,
            street: values.street,
            houseNumber: values.houseNumber,
            zipCode: values.zipCode,
            city: values.city,
            country: values.country,
            email: values.email,
            phone: values.phone,
            remarks: values.remarks,
          },
        },
        // onError toasts every failure itself; without this the global middleware would toast too.
        withLocalErrorHandling
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Volunteers] });
      showSuccess(t("volunteers.edit.successfullyUpdated"));
    },
    // Without this the backend's username-uniqueness/username-locked rejections would be swallowed
    // and the form would just sit there: no toast, no change, no clue. The form stays dirty so the
    // user can fix it.
    onError: async (error) => {
      const errorCode = await getApiErrorCode(error);
      showError(
        errorCode === ApiErrorCode.VolunteerUsernameAlreadyTaken
          ? t("volunteers.usernameAlreadyTaken")
          : errorCode === ApiErrorCode.VolunteerUsernameLockedByPortalAccount
            ? t("volunteers.usernameLockedByPortalAccount")
            : t("volunteers.edit.saveFailed")
      );
    },
  });

  const deleteVolunteerMutation = useMutation({
    mutationFn: () => volunteersApi.deleteVolunteer({ id }, withLocalErrorHandling),
    onSuccess: () => {
      // refetchType "none" is what keeps this from refetching the volunteer that no longer exists:
      // the [Volunteers, id] detail query sits under this prefix and is still mounted by the parent
      // VolunteerDetail, so a default ("active") invalidate would fire a GET right here — 404,
      // toasted by the global error middleware behind the success message, and not cancellable by
      // the redirect. Marking stale is enough: the list page refetches when it mounts.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Volunteers], refetchType: "none" });
      showSuccess(t("volunteers.edit.successfullyDeleted"));
      void navigate(systemSettingsPath.byTab(SystemSettingsTab.Volunteers));
    },
    onError: () => showError(t("volunteers.edit.deleteFailed")),
  });

  const validationSchema = useMemo(() => volunteerValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<VolunteerFormType>({
    initialValues: {
      username: volunteer.username,
      salutation: volunteer.salutation ?? null,
      title: volunteer.title ?? null,
      lastName: volunteer.lastName,
      firstName: volunteer.firstName,
      street: volunteer.street,
      houseNumber: volunteer.houseNumber,
      zipCode: volunteer.zipCode,
      city: volunteer.city,
      country: volunteer.country,
      email: volunteer.email,
      phone: volunteer.phone ?? null,
      remarks: volunteer.remarks ?? null,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await updateVolunteerMutation.mutateAsync(values);
    },
  });

  const handleDelete = () => {
    showConfirmationDialog({
      dialogTitleText: t("volunteers.edit.deleteConfirmTitle"),
      dialogText: t("volunteers.edit.deleteConfirmText"),
      agreeText: t("common.yes"),
      discardText: t("common.no"),
      onClose: (confirmed) => {
        if (confirmed) {
          // Drop any pending edits before the request goes out: they are about to be deleted along
          // with the volunteer, and while the form is dirty NavigationBlocker would stop the
          // post-delete redirect with an unsaved-changes prompt for a volunteer that no longer exists.
          formik.resetForm();
          deleteVolunteerMutation.mutate();
        }
      },
    });
  };

  return {
    formik,
    dropdownOptions,
    handleDelete,
    isDeleting: deleteVolunteerMutation.isPending,
  };
}
